// src/services/notificationPreferences.ts
import { Preferences } from "@capacitor/preferences";
import { supabase } from "../supabaseClient";

const STORAGE_KEY = "notification_preferences_v1";

export type NotificationCategory = "matches" | "chat" | "posts";

export type NotificationPreferences = {
  enabled: boolean;
  categories: Record<NotificationCategory, boolean>;
  reminderMinutes: number; // délai de rappel avant un match/entraînement
};

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  enabled: false,
  categories: {
    matches: true,
    chat: true,
    posts: true,
  },
  reminderMinutes: 60,
};

export const REMINDER_OPTIONS = [15, 30, 60, 120, 1440] as const;

function mergeWithDefaults(parsed: any): NotificationPreferences {
  return {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...parsed,
    categories: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.categories,
      ...(parsed?.categories || {}),
    },
  };
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  const { value } = await Preferences.get({ key: STORAGE_KEY });
  if (!value) return { ...DEFAULT_NOTIFICATION_PREFERENCES };
  try {
    return mergeWithDefaults(JSON.parse(value));
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFERENCES };
  }
}

/**
 * Récupère les préférences côté serveur (source utilisée par la fonction d'envoi
 * de rappels). Retourne null si l'utilisateur n'a jamais rien configuré (aucun device).
 */
export async function pullServerPreferences(): Promise<NotificationPreferences | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data } = await supabase
    .from("notification_preferences")
    .select("enabled, matches, chat, posts, reminder_minutes")
    .eq("user_id", user.id)
    .maybeSingle();

  if (!data) return null;

  return mergeWithDefaults({
    enabled: data.enabled,
    reminderMinutes: data.reminder_minutes,
    categories: {
      matches: data.matches,
      chat: data.chat,
      posts: data.posts,
    },
  });
}

/**
 * Bootstrap: si cet appareil n'a jamais eu de préférences locales, on tente de
 * reprendre celles déjà configurées côté serveur (autre appareil) avant de
 * retomber sur les valeurs par défaut.
 */
export async function bootstrapNotificationPreferences(): Promise<NotificationPreferences> {
  const { value } = await Preferences.get({ key: STORAGE_KEY });
  if (value) return getNotificationPreferences();

  const server = await pullServerPreferences();
  if (server) {
    await setNotificationPreferences(server);
    return server;
  }
  return getNotificationPreferences();
}

async function pushPreferencesToServer(prefs: NotificationPreferences): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  await supabase.from("notification_preferences").upsert(
    {
      user_id: user.id,
      enabled: prefs.enabled,
      matches: prefs.categories.matches,
      chat: prefs.categories.chat,
      posts: prefs.categories.posts,
      reminder_minutes: prefs.reminderMinutes,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id" }
  );
}

export async function setNotificationPreferences(prefs: NotificationPreferences): Promise<void> {
  await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(prefs) });
}

type Listener = (prefs: NotificationPreferences) => void;
const listeners = new Set<Listener>();

export function subscribeNotificationPreferences(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function updateNotificationPreferences(
  patch: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
  const current = await getNotificationPreferences();
  const next: NotificationPreferences = {
    ...current,
    ...patch,
    categories: { ...current.categories, ...(patch.categories || {}) },
  };
  await setNotificationPreferences(next);
  listeners.forEach((l) => l(next));
  void pushPreferencesToServer(next);
  return next;
}
